/**
 * VENTION B2B Multi-Step Form Controller
 * Handles step navigation, validation, UTM capture & analytics
 * Author: Antigravity / Vention Dev Team
 */

(function () {
  'use strict';

  // ─── Config ───────────────────────────────────────────────
  const STEPS_COUNT = 3;
  const PROGRESS_PER_STEP = [33, 66, 100]; // % fill per step

  // ─── DOM References ───────────────────────────────────────
  const form        = document.getElementById('B2BLeadForm');
  const progressFill = document.getElementById('b2bProgressFill');
  const progressBar  = document.getElementById('b2bProgressBar');

  if (!form) return; // Guard: form may not be on this page

  // Steps
  const steps = [
    document.getElementById('b2bStep1'),
    document.getElementById('b2bStep2'),
    document.getElementById('b2bStep3'),
  ].filter(Boolean);

  // Step indicators
  const indicators = [
    document.getElementById('b2bStep1Indicator'),
    document.getElementById('b2bStep2Indicator'),
    document.getElementById('b2bStep3Indicator'),
  ].filter(Boolean);

  let currentStep = 1;

  // ─── Capture UTM params ───────────────────────────────────
  function captureUTM() {
    const sourceInput = document.getElementById('B2BForm-source');
    if (!sourceInput) return;

    const params = new URLSearchParams(window.location.search);
    const parts = [];

    ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'].forEach(key => {
      const val = params.get(key) || sessionStorage.getItem(key);
      if (val) {
        parts.push(`${key}=${val}`);
        // Persist UTM in session for multi-page flows
        sessionStorage.setItem(key, val);
      }
    });

    // Add referrer
    if (document.referrer) {
      parts.push(`referrer=${encodeURIComponent(document.referrer)}`);
    }

    sourceInput.value = parts.join(' | ') || 'Direct';
  }

  // ─── Update Progress Bar ───────────────────────────────────
  function updateProgress(step) {
    const pct = PROGRESS_PER_STEP[step - 1] || 33;

    if (progressFill) {
      progressFill.style.width = pct + '%';
    }

    if (progressBar) {
      progressBar.setAttribute('aria-valuenow', pct);
    }

    // Update step indicators
    indicators.forEach((indicator, idx) => {
      const stepNum = idx + 1;
      indicator.classList.remove('b2b-progress-step--active', 'b2b-progress-step--done');

      const dot = indicator.querySelector('.b2b-progress-step__dot');

      if (stepNum < step) {
        indicator.classList.add('b2b-progress-step--done');
        if (dot) dot.textContent = '✓';
      } else if (stepNum === step) {
        indicator.classList.add('b2b-progress-step--active');
        if (dot) dot.textContent = stepNum;
      } else {
        if (dot) dot.textContent = stepNum;
      }
    });
  }

  // ─── Show a Step ──────────────────────────────────────────
  function showStep(stepNum) {
    steps.forEach((el, idx) => {
      const isActive = idx + 1 === stepNum;
      el.classList.toggle('b2b-form-step--active', isActive);
    });

    currentStep = stepNum;
    updateProgress(stepNum);

    // Scroll to form card on mobile
    const formCard = document.getElementById('b2bLeadFormCard');
    if (formCard && window.innerWidth < 900) {
      formCard.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    // Focus first input in active step
    requestAnimationFrame(() => {
      const activeStep = steps[stepNum - 1];
      if (!activeStep) return;
      const firstInput = activeStep.querySelector('input, select, textarea');
      if (firstInput) firstInput.focus();
    });
  }

  // ─── Validate Step Fields ──────────────────────────────────
  function validateStep(stepNum) {
    const stepEl = steps[stepNum - 1];
    if (!stepEl) return true;

    let isValid = true;

    // Clear previous errors
    stepEl.querySelectorAll('.b2b-field-error').forEach(el => el.remove());
    stepEl.querySelectorAll('.b2b-field__input--error').forEach(el => {
      el.classList.remove('b2b-field__input--error');
    });

    // Step 1: Name & Email
    if (stepNum === 1) {
      const nameInput  = document.getElementById('B2BForm-name');
      const emailInput = document.getElementById('B2BForm-email');

      if (nameInput && nameInput.value.trim().length < 2) {
        showFieldError(nameInput, 'Por favor ingresa tu nombre completo.');
        isValid = false;
      }

      if (emailInput && !isValidEmail(emailInput.value.trim())) {
        showFieldError(emailInput, 'Ingresa un correo electrónico válido.');
        isValid = false;
      }
    }

    // Step 2: Business Type
    if (stepNum === 2) {
      const selected = stepEl.querySelector('input[name="b2b_biz_type_radio"]:checked');
      if (!selected) {
        const group = stepEl.querySelector('.b2b-option-cards');
        if (group) {
          showGroupError(group, 'Por favor selecciona tu tipo de negocio.');
        }
        isValid = false;
      }
    }

    // Step 3: Volume
    if (stepNum === 3) {
      const volumeSelect = document.getElementById('B2BForm-volume');
      if (volumeSelect && !volumeSelect.value) {
        showFieldError(volumeSelect, 'Por favor selecciona tu volumen mensual.');
        isValid = false;
      }
    }

    return isValid;
  }

  // ─── Field Error Helpers ───────────────────────────────────
  function showFieldError(input, message) {
    input.classList.add('b2b-field__input--error');
    const error = document.createElement('span');
    error.className = 'b2b-field-error';
    error.setAttribute('role', 'alert');
    error.textContent = message;
    input.parentNode.appendChild(error);
  }

  function showGroupError(container, message) {
    const error = document.createElement('span');
    error.className = 'b2b-field-error';
    error.setAttribute('role', 'alert');
    error.textContent = message;
    container.parentNode.insertBefore(error, container.nextSibling);
  }

  function isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  // ─── Business Type Option Cards ────────────────────────────
  function initOptionCards() {
    const radios     = form.querySelectorAll('input[name="b2b_biz_type_radio"]');
    const hiddenInput = document.getElementById('B2BForm-biz-type');
    const cards      = form.querySelectorAll('.b2b-option-card');

    radios.forEach(radio => {
      radio.addEventListener('change', () => {
        // Update hidden input for Shopify form submission
        if (hiddenInput) hiddenInput.value = radio.value;

        // Visual update for all cards
        cards.forEach(card => {
          card.classList.remove('b2b-option-card--selected');
        });

        const parentCard = radio.closest('.b2b-option-card');
        if (parentCard) parentCard.classList.add('b2b-option-card--selected');
      });
    });

    // Also handle keyboard selection
    cards.forEach(card => {
      card.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          const radio = card.querySelector('input[type="radio"]');
          if (radio) {
            radio.checked = true;
            radio.dispatchEvent(new Event('change', { bubbles: true }));
          }
        }
      });
    });
  }

  // ─── Next / Prev Button Handlers ──────────────────────────
  function initNavigation() {
    // Next buttons
    form.querySelectorAll('[data-next]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const targetStep = parseInt(btn.dataset.next, 10);
        const fromStep   = targetStep - 1;

        if (!validateStep(fromStep)) return;

        // Analytics event (if GTM/GA4 available)
        trackStepComplete(fromStep);

        showStep(targetStep);
      });
    });

    // Prev buttons
    form.querySelectorAll('[data-prev]').forEach(btn => {
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        const targetStep = parseInt(btn.dataset.prev, 10);
        showStep(targetStep);
      });
    });
  }

  // ─── Form Submit Handler ───────────────────────────────────
  function initSubmit() {
    const submitBtn  = document.getElementById('b2bSubmitBtn');
    const submitText = submitBtn?.querySelector('.b2b-submit-text');
    const loadingTxt = submitBtn?.querySelector('.b2b-submit-loading');

    if (!submitBtn) return;

    form.addEventListener('submit', (e) => {
      // Validate last step before allowing native Shopify submit
      if (!validateStep(currentStep)) {
        e.preventDefault();
        return;
      }

      // Show loading state
      if (submitText)  submitText.hidden = true;
      if (loadingTxt)  loadingTxt.hidden = false;
      if (submitBtn)   submitBtn.disabled = true;

      // Track conversion event
      trackFormSubmit();
    });
  }

  // ─── Analytics Helpers ────────────────────────────────────
  function trackStepComplete(step) {
    try {
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'b2b_form_step_complete', {
          event_category: 'B2B Lead Form',
          event_label: `Step ${step} Completed`,
          value: step,
        });
      }

      if (typeof window.fbq === 'function') {
        window.fbq('trackCustom', 'B2BFormStepComplete', { step: step });
      }

      if (typeof window.dataLayer !== 'undefined') {
        window.dataLayer.push({
          event: 'b2b_form_step',
          step_number: step,
        });
      }
    } catch (_) {/* silent */}
  }

  function trackFormSubmit() {
    try {
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'generate_lead', {
          event_category: 'B2B Lead Form',
          event_label: 'Form Submitted',
          currency: 'MXN',
          value: 1,
        });
      }

      if (typeof window.fbq === 'function') {
        window.fbq('track', 'Lead', { content_name: 'B2B Distributor Form' });
      }

      if (typeof window.dataLayer !== 'undefined') {
        window.dataLayer.push({ event: 'b2b_lead_submitted' });
      }
    } catch (_) {/* silent */}
  }

  // ─── Live Field Validation (UX) ───────────────────────────
  function initLiveValidation() {
    const emailInput = document.getElementById('B2BForm-email');
    if (!emailInput) return;

    emailInput.addEventListener('blur', () => {
      const prev = emailInput.parentNode.querySelector('.b2b-field-error');
      if (prev) prev.remove();
      emailInput.classList.remove('b2b-field__input--error');

      if (emailInput.value && !isValidEmail(emailInput.value)) {
        showFieldError(emailInput, 'Ingresa un correo electrónico válido.');
      }
    });
  }

  // ─── CSS Error Styles (injected dynamically) ───────────────
  function injectErrorStyles() {
    const style = document.createElement('style');
    style.textContent = `
      .b2b-field__input--error {
        border-color: #EF4444 !important;
        box-shadow: 0 0 0 3px rgba(239, 68, 68, 0.15) !important;
      }
      .b2b-field-error {
        display: block;
        font-size: 0.75rem;
        color: #EF4444;
        margin-top: 4px;
        padding-left: 4px;
        animation: errorShake 0.3s ease;
      }
      @keyframes errorShake {
        0%, 100% { transform: translateX(0); }
        25%  { transform: translateX(-4px); }
        75%  { transform: translateX(4px); }
      }
    `;
    document.head.appendChild(style);
  }

  // ─── Prevent implicit Enter-key submit on non-final steps ──
  // Un <button type="submit"> oculto sigue siendo el "default button"
  // del <form>. Sin esto, Enter en el Paso 1/2 envia el lead incompleto.
  function initEnterKeyGuard() {
    form.addEventListener('keydown', (e) => {
      if (e.key !== 'Enter') return;
      if (e.target.tagName === 'TEXTAREA') return; // permitir salto de linea

      const activeStep = steps[currentStep - 1];
      if (!activeStep) return;

      const isFinalStep = currentStep === steps.length;
      if (isFinalStep) return; // dejar que el submit real ocurra en el ultimo paso

      e.preventDefault();
      const nextBtn = activeStep.querySelector('[data-next]');
      if (nextBtn) nextBtn.click();
    });
  }

  // ─── Init ─────────────────────────────────────────────────
  function init() {
    injectErrorStyles();
    captureUTM();
    initOptionCards();
    initNavigation();
    initEnterKeyGuard();
    initSubmit();
    initLiveValidation();
    updateProgress(1);
  }

  // Run when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
